import React from "react";
import { Menu } from "antd";
import { Link, useLocation } from "react-router-dom";
import {
  SettingOutlined,
  HomeOutlined,
  WechatWorkOutlined,
  UserOutlined,
  NotificationOutlined,
  TeamOutlined,
  SettingFilled,
  BuildFilled,
} from "@ant-design/icons";
import {
  MdPhonelinkSetup,
  MdManageAccounts,
} from "react-icons/md";
import { TbBrandGoogleAnalytics } from "react-icons/tb";
import { TfiReload } from "react-icons/tfi";
import { FaMeta } from "react-icons/fa6";
import type { MenuProps } from "antd";
import { useAuthOrg } from "../../context/AuthOrgContext";
import { ORG_ID } from "../../utils/constant";
import { hasRolePermission, RolePermission } from "../../utils/roles";

interface SidebarMenuProps {
  collapsed?: boolean;
}

const SideMenu: React.FC<SidebarMenuProps> = ({ collapsed }) => {
  const location = useLocation();
  const { user, currentOrg } = useAuthOrg();


  const can = (permission: RolePermission) => hasRolePermission(user?.userRole, permission);
  const isSelfServed = user?.userMode === 'self-served';
  const canViewMetaMenu = can('viewMetaAdAccounts') || can('viewAdAnalytics') || can('viewBotAnalytics') || can('manageConversionEvents') || can('manageRetargeting');
  const canViewCampaignMenu = can('viewCampaignDashboard') || can('viewLeads') || can('viewBillingReport');
  const filterMenuItems = (items: MenuProps['items']): MenuProps['items'] =>
    items?.filter((item): item is NonNullable<typeof item> => item !== null) || [];

  const allMenuItemsExceptLogout: MenuProps['items'] = [
    // Full access user items (self_served/parent orgs)
    can('viewHome') ? {
      key: "/home",
      icon: <HomeOutlined className="text-lg" />,
      label: (
        <Link
          className={`menu-item ${location.pathname === "/home" ? "selected" : ""}`}
          to="/home"
        >
          Home
        </Link>
      ),
    } : null,

    can('accessSetup') ? {
      key: "/setup",
      icon: <MdPhonelinkSetup className="text-lg" />,
      label: "Setup",
      children: filterMenuItems([
        {
          key: "/setup-meta",
          icon: <MdPhonelinkSetup className="text-lg" />,
          label: (
            <Link
              className={`menu-item ${location.pathname === "/setup" ? "selected" : ""}`}
              to="/setup"
            >
              Meta Setup
            </Link>
          )
        },
        isSelfServed && currentOrg?.orgId !== ORG_ID ? {
          key: "/verismart-setup",
          icon: <MdPhonelinkSetup className="text-lg" />,
          label: (
            <Link
              className={`menu-item ${location.pathname === "/verismart-setup" ? "selected" : ""}`}
              to="/verismart-setup"
            >
              Brand KYC
            </Link>
          )
        } : null,
      ])
    } : null,

    canViewMetaMenu ? {
      key: "ctc-ads-meta",
      icon: <FaMeta className="text-lg" />,
      label: "CTC Ads - Meta",
      children: filterMenuItems([
        can('viewMetaAdAccounts') ? {
          key: "/ctc-ads-meta/ad-accounts",
          icon: <MdManageAccounts className="text-lg" />,
          label: (
            <Link
              className={`menu-item ${location.pathname === "/ctc-ads-meta/ad-accounts" ? "selected" : ""}`}
              to="/ctc-ads-meta/ad-accounts"
            >
              Meta Ad Accounts
            </Link>
          ),
        } : null,
        can('viewAdAnalytics') ? {
          key: "/ctc-ads-meta/ad-analytics",
          icon: <TbBrandGoogleAnalytics className="text-lg" />,
          label: (
            <Link
              className={`menu-item ${location.pathname === "/ctc-ads-meta/ad-analytics" ? "selected" : ""}`}
              to="/ctc-ads-meta/ad-analytics"
            >
              Ad Analytics
            </Link>
          ),
        } : null,
        can('viewBotAnalytics') ? {
          key: "/ctc-ads-meta/bot-analytics",
          icon: <NotificationOutlined className="text-lg" />,
          label: (
            <Link
              className={`menu-item ${location.pathname === "/ctc-ads-meta/bot-analytics" ? "selected" : ""}`}
              to="/ctc-ads-meta/bot-analytics"
            >
              Bot Analytics
            </Link>
          ),
        } : null,
        can('manageConversionEvents') ? {
          key: "/ctc-ads-meta/conversion-events",
          icon: <TfiReload className="text-lg" />,
          label: (
            <Link
              className={`menu-item ${location.pathname === "/ctc-ads-meta/conversion-events" ? "selected" : ""}`}
              to="/ctc-ads-meta/conversion-events"
            >
              Conversion Events
            </Link>
          ),
        } : null,
        can('manageRetargeting') ? {
          key: "/ctc-ads-meta/retargeting",
          icon: <WechatWorkOutlined className="text-lg" />,
          label: (
            <Link
              className={`menu-item ${location.pathname === "/ctc-ads-meta/retargeting" ? "selected" : ""}`}
              to="/ctc-ads-meta/retargeting"
            >
              Retargeting
            </Link>
          ),
        } : null,
      ]),
    } : null,


    canViewCampaignMenu ? {
      key: "dashboard-menu",
      icon: <NotificationOutlined className="text-lg" />,
      label: "Campaign Management",
      children: filterMenuItems([
        ...(can('viewCampaignDashboard') ? [{
          key: "/campaign-management",
          icon: <NotificationOutlined className="text-lg" />,
          label: (
            <Link
              className={`menu-item ${location.pathname === "/campaign-management" ? "selected" : ""}`}
              to="/campaign-management"
            >
              Dashboard
            </Link>
          ),
        }] : []),
        can('viewLeads') ? {
          key: "/leads",
          icon: <TeamOutlined className="text-lg" />,
          label: (
            <Link
              className={`menu-item ${location.pathname === "/leads" ? "selected" : ""}`}
              to="/leads"
            >
              Leads
            </Link>
          ),
        } : null,
        can('viewBillingReport') ? {
          key: "/billing",
          icon: <TbBrandGoogleAnalytics className="text-lg" />,
          label: (
            <Link
              className={`menu-item ${location.pathname === "/billing" ? "selected" : ""}`}
              to="/billing"
            >
              Billing Report
            </Link>
          )
        } : null,
      ])
    } : null,

    // Common items for both user types
    can('viewAudience') ? {
      key: "/audience",
      icon: <TeamOutlined className="text-lg" />,
      label: (
        <Link
          className={`menu-item ${location.pathname === "/audience" ? "selected" : ""}`}
          to="/audience"
        >
          Premium Audience
        </Link>
      ),
    } : null,

    can('viewConfig') && !isSelfServed ? {
      key: "/setting",
      icon: <SettingFilled className="text-lg" />,
      label: "Config",
      children: [
        {
          key: "/setting/organizations",
          icon: <BuildFilled className="text-lg" />,
          label: (
            <Link
              className={`menu-item ${location.pathname === "/setting/organizations" ? "selected" : ""}`}
              to="/setting/organizations"
            >
              Add Accounts
            </Link>
          )
        },
        {
          key: "/setting/users",
          icon: <MdPhonelinkSetup className="text-lg" />,
          label: (
            <Link
              className={`menu-item ${location.pathname === "/setting/users" ? "selected" : ""}`}
              to="/setting/users"
            >
              Add Users
            </Link>
          )
        },
        {
          key: "/verismart-setup",
          icon: <MdPhonelinkSetup className="text-lg" />,
          label: (
            <Link
              className={`menu-item ${location.pathname === "/verismart-setup" ? "selected" : ""}`}
              to="/verismart-setup"
            >
              Add Brand
            </Link>
          )
        }
      ]
    } : null,

    // {
    //   key: "/help",
    //   icon: <QuestionCircleOutlined className="text-lg" />,
    //   label: (
    //     <Link
    //       className={`menu-item ${location.pathname === "/help" ? "selected" : ""}`}
    //       to="/help"
    //     >
    //       Help
    //     </Link>
    //   ),
    // },

    can('viewSettings') ? {
      key: "/settings",
      icon: <SettingOutlined className="text-lg" />,
      label: (
        <Link
          className={`menu-item ${location.pathname === "/settings" ? "selected" : ""}`}
          to="/settings"
        >
          Settings
        </Link>
      ),
    } : null,
  ];

  const logoutMenuItem = {
    key: "/logout",
    icon: <UserOutlined className="text-lg" />,
    label: (
      <Link
        className={`menu-item ${location.pathname === "/logout" ? "selected" : ""}`}
        to="/logout"
      >
        Logout
      </Link>
    ),
  };

  // Filter out null items
  const mainMenuItems = allMenuItemsExceptLogout.filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <>
      {/* Scrollable main menu */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <Menu
          selectedKeys={[location.pathname]}
          className="site-menu border-0"
          items={mainMenuItems}
          mode="inline"
          inlineCollapsed={collapsed}
          defaultOpenKeys={canViewMetaMenu ? ["ctc-ads-meta"] : []}
        />
      </div>

      {/* Fixed logout at bottom */}
      <div className="mt-auto border-t border-gray-700">
        <Menu
          selectedKeys={[location.pathname]}
          className="site-menu border-0"
          items={[logoutMenuItem]}
          mode="inline"
          inlineCollapsed={collapsed}
        />
      </div>
    </>
  );
};

export default SideMenu;
